import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { PLAN_OPTIONS, PlanType } from '../../../services/tipos';

interface StepPlanProps {
  tipoPlano: 'PRO' | 'AVANCADO';
  vehicleCount: number;
  setVehicleCount: (count: number) => void;
  selectedPlan: PlanType;
  setSelectedPlan: (plan: PlanType) => void;
  minVehicleLimit: number;
  calcularPrecoMensal: (count: number, plano: string) => number;
  onNext: () => void;
}

const StepPlan: React.FC<StepPlanProps> = ({
  tipoPlano,
  vehicleCount,
  setVehicleCount,
  selectedPlan,
  setSelectedPlan,
  minVehicleLimit,
  calcularPrecoMensal,
  onNext,
}) => {
  const isDecreaseDisabled = vehicleCount <= minVehicleLimit;

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const getCycleLabel = (plan: typeof PLAN_OPTIONS[number]) => {
    switch (plan.cycleMonths) {
      case 1: return 'cobrado todo mês';
      case 3: return 'a cada 3 meses';
      case 6: return 'a cada 6 meses';
      case 12: return 'cobrado 1x por ano';
      default: return '';
    }
  };

  const getEconomy = (plan: typeof PLAN_OPTIONS[number]) => {
    if (plan.discount === 0) return 0;
    const precoMensalBase = calcularPrecoMensal(vehicleCount, tipoPlano);
    const precoSemDesconto = precoMensalBase * plan.cycleMonths;
    const precoComDesconto = precoMensalBase * (1 - plan.discount) * plan.cycleMonths;
    return precoSemDesconto - precoComDesconto;
  };

  const getEconomyLabel = (plan: typeof PLAN_OPTIONS[number]) => {
    switch (plan.cycleMonths) {
      case 3: return 'no trimestre';
      case 6: return 'por ciclo';
      case 12: return 'por ano';
      default: return '';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="text-[26px] font-extrabold text-[#1F467E] mb-1.5">
          Plano {tipoPlano === 'AVANCADO' ? 'Avançado' : 'Pro'}
        </h1>
        <p className="text-[15px] text-[#94a3b8]">Configure sua assinatura em poucos passos</p>
      </div>

      {/* Vehicle selector */}
      <div className="bg-white rounded-2xl py-4 px-7 mb-5 flex items-center justify-between" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <span className="text-base font-semibold text-[#475569]">Quantos veículos?</span>
        <div className="flex items-center gap-4">
          <button
            onClick={() => !isDecreaseDisabled && setVehicleCount(vehicleCount - 5)}
            disabled={isDecreaseDisabled}
            className={`w-10 h-10 rounded-[10px] border-2 flex items-center justify-center text-xl font-semibold ${
              isDecreaseDisabled
                ? 'bg-[#f1f5f9] border-[#e2e8f0] text-[#cbd5e1] cursor-not-allowed'
                : 'bg-[#f1f5f9] border-[#e2e8f0] text-[#94a3b8] cursor-pointer hover:border-[#94a3b8]'
            }`}
          >
            <Minus size={18} />
          </button>
          <div className="text-center">
            <div className="text-[40px] font-black text-[#1F467E] leading-none min-w-[56px]">{vehicleCount}</div>
            <div className="text-[11px] text-[#94a3b8] mt-0.5">veículos</div>
          </div>
          <button
            onClick={() => setVehicleCount(vehicleCount + 5)}
            className="w-10 h-10 rounded-[10px] border-2 bg-[#f0f4ff] border-[#1F467E] text-[#1F467E] flex items-center justify-center text-xl font-semibold cursor-pointer hover:bg-[#e0eaff]"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {/* Cycle selection */}
      <div className="text-base font-semibold text-[#475569] text-center mb-4">Escolha o ciclo de pagamento</div>
      <div className="grid grid-cols-2 gap-3 mb-7">
        {PLAN_OPTIONS.map((plan) => {
          const precoMensal = calcularPrecoMensal(vehicleCount, tipoPlano) * (1 - plan.discount);
          const precoTotal = precoMensal * plan.cycleMonths;
          const economy = getEconomy(plan);
          const isSelected = selectedPlan === plan.id;
          const isFeatured = plan.id === 'SEMIANNUAL';
          const isAnnual = plan.id === 'ANNUAL';

          return (
            <div key={plan.id} className="relative">
              {isFeatured && (
                <div className="absolute -top-[10px] left-3 bg-[#1F467E] text-white text-[10px] font-extrabold py-0.5 px-2.5 rounded-full z-10 tracking-[0.3px]">
                  MAIS POPULAR
                </div>
              )}
              {isAnnual && (
                <div className="absolute -top-[10px] right-3 bg-[#16a34a] text-white text-[10px] font-extrabold py-0.5 px-2.5 rounded-full z-10 tracking-[0.3px]">
                  MELHOR PREÇO
                </div>
              )}
              <button
                onClick={() => setSelectedPlan(plan.id)}
                className={`w-full rounded-[14px] py-3.5 px-4 flex flex-col items-center justify-center text-center cursor-pointer transition-all min-h-[110px] ${
                  isSelected
                    ? 'border-2 border-[#1F467E] bg-[#fafbff]'
                    : 'border-2 border-[#e2e8f0] bg-white hover:border-[#93a8c9]'
                }`}
                style={
                  isFeatured && isSelected
                    ? { boxShadow: '0 4px 16px rgba(31,70,126,0.1)' }
                    : { boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }
                }
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[15px] font-bold ${isSelected ? 'text-[#1F467E]' : 'text-[#374151]'}`}>
                    {plan.name}
                  </span>
                  {plan.discount > 0 && (
                    <span
                      className={`text-[11px] font-extrabold py-0.5 px-2 rounded-full ${
                        plan.discount >= 0.15
                          ? 'bg-[#16a34a] text-white'
                          : 'bg-[#dcfce7] text-[#16a34a]'
                      }`}
                    >
                      -{plan.discount * 100}%
                    </span>
                  )}
                </div>
                <div className={`font-extrabold ${isFeatured && isSelected ? 'text-xl text-[#1F467E]' : 'text-lg'} ${isSelected ? 'text-[#1F467E]' : 'text-[#374151]'}`}>
                  {formatCurrency(precoTotal)}
                </div>
                <div className="text-[13px] text-[#94a3b8] mt-0.5">
                  {getCycleLabel(plan)}
                  {plan.cycleMonths > 1 && ` (${formatCurrency(precoMensal)}/mês)`}
                </div>
                {economy > 0 && (
                  <div className="text-[12px] text-[#16a34a] font-bold mt-1">
                    Economize {formatCurrency(economy)} {getEconomyLabel(plan)}
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* CTA — sticky para ficar sempre visível sem precisar rolar */}
      <div className="sticky bottom-0 z-10 -mx-6 px-6 pt-3 pb-3 bg-[#f8fafc] shadow-[0_-6px_16px_rgba(0,0,0,0.06)]">
        <button
          onClick={onNext}
          className="w-full py-[18px] bg-[#1F467E] rounded-[14px] text-center text-white font-extrabold text-[17px] cursor-pointer border-none hover:bg-[#162f56] transition-colors"
        >
          Continuar
        </button>
      </div>
    </div>
  );
};

export default StepPlan;
